import { Map } from "immutable";
import { ComponentType } from "react";
import { InjectedFormProps } from "redux-form";
import { reduxForm, SubmissionError } from "redux-form/immutable";

import { queryClient } from "services";

import { fetchSelectedSchoolQueryOptions } from "seneca-common/features/schools/school-selection/queries";
import { putMySchoolInfo } from "seneca-common/features/schools/service/mutations";
import { compose, toJS } from "seneca-common/utils/components/container-utils";
import {
  validatePhoneNumber,
  validateStringField
} from "seneca-common/utils/functions/validationFunctions";

import { linkToDashboard } from "features/dashboard/links";
import { linkToParentDashboard } from "features/parents/links";
import {
  userAccountType as ACCOUNT_TYPE,
  setRegionPreferenceAndRefresh,
  updateAttributes
} from "features/user";
import { ROUTES } from "routing/routes";

import {
  formName,
  PERSONAL_INFORMATION_UPDATE_ID,
  regionFieldName
} from "./consts";

const validate = (valuesIn: any) => {
  const { firstName, lastName, phoneNumber } = valuesIn.toJS();
  return {
    firstName: validateStringField("first name")(firstName),
    lastName: validateStringField("last name")(lastName),
    phoneNumber: validatePhoneNumber(phoneNumber)
  };
};

const userInfoToUpdate = (
  formValues: any,
  initialValues: any,
  isManaged: boolean
): Record<string, string> | null | undefined => {
  const userInfoFormMap: any = isManaged
    ? {
        nickname: "nickname",
        teacherRole: "teacherRole",
        teacherSubjects: "teacherSubjects"
      }
    : {
        firstName: "givenName",
        lastName: "familyName",
        nickname: "nickname",
        phoneNumber: "phoneNumber",
        accountType: "type",
        yearGroup: "yearGroup",
        teacherRole: "teacherRole",
        teacherSubjects: "teacherSubjects"
      };

  const userInfo = Object.keys(userInfoFormMap).reduce(
    (userInfo: any, fieldName) => {
      const clientAttributeName = userInfoFormMap[fieldName];
      const formValue = formValues.get(fieldName);

      if (formValue && initialValues.get(fieldName) !== formValue) {
        userInfo[clientAttributeName] = formValue;
      }

      return userInfo;
    },
    {}
  );
  return Object.keys(userInfo).length ? userInfo : null;
};

const regionPreferenceToUpdate = (
  formValues: any,
  initialValues: any
): string | null | undefined => {
  const region = formValues.get(regionFieldName);
  return region !== initialValues.get(regionFieldName) ? region : null;
};

const getRedirectLink = (
  formValues: any,
  initialValues: any
): string | null | undefined => {
  const accountType = formValues.get("accountType");

  if (initialValues.get("accountType") !== accountType) {
    return accountType === ACCOUNT_TYPE.schoolteacher
      ? ROUTES.TEACHER.buildPath({})
      : accountType === ACCOUNT_TYPE.schoolparent
        ? linkToParentDashboard()
        : linkToDashboard();
  }
};

const withForm = reduxForm({
  form: formName,
  // @ts-ignore
  validate,
  enableReinitialize: true,
  destroyOnUnmount: false,
  onSubmit: async (formValues: Map<string, any>, dispatch: any, props: any) => {
    const { initialValues, navigate, isManaged } = props;
    try {
      const userInfo = userInfoToUpdate(formValues, initialValues, isManaged);

      if (userInfo) {
        await dispatch(
          updateAttributes(userInfo, PERSONAL_INFORMATION_UPDATE_ID)
        );
      }

      const region = regionPreferenceToUpdate(formValues, initialValues);

      if (region) {
        await dispatch(setRegionPreferenceAndRefresh(region));
      }

      const school = formValues.get("school") as Map<string, any>;
      if (
        school &&
        school.get("schoolId") !== initialValues.getIn(["school", "schoolId"])
      ) {
        const schoolId = school.get("cantFindOption")
          ? undefined
          : school.get("schoolId");
        await putMySchoolInfo(schoolId).then(() => {
          queryClient.invalidateQueries({
            queryKey: fetchSelectedSchoolQueryOptions().queryKey
          });
        });
      }

      const redirectLink = getRedirectLink(formValues, initialValues);

      if (redirectLink) {
        navigate(redirectLink);
      }

      return userInfo;
    } catch (error: any) {
      console.log("Error updating personal info or preferences", error);
      throw new SubmissionError({
        _error:
          "Sorry, we were unable to update your info. Please try again later."
      });
    }
  }
});

// @ts-ignore
const container: <PropsT>(
  comp: ComponentType<PropsT & InjectedFormProps>
) => ComponentType<PropsT> = compose(toJS, withForm);

export default container;
